import { useState } from "react";
import { Check, CalendarClock, X, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const STATUS_STYLE = {
  pending: "bg-[#D98E73]/15 text-[#B5794E]",
  confirmed: "bg-[#5C8A72]/15 text-[#5C8A72]",
  reschedule_proposed: "bg-[#144F75]/10 text-[#144F75]",
  cancelled: "bg-red-100 text-red-500",
};
const STATUS_LABEL = {
  pending: "Requested",
  confirmed: "Confirmed",
  reschedule_proposed: "Awaiting client",
  cancelled: "Cancelled",
};

export function StatusBadge({ status }) {
  const s = status || "pending";
  return <span className={`text-xs px-2.5 py-1 rounded-full ${STATUS_STYLE[s] || STATUS_STYLE.pending}`} data-testid="appt-status">{STATUS_LABEL[s] || s}</span>;
}

// Confirm + Reschedule controls for enhancers & location managers.
export default function AppointmentActions({ booking, onChanged }) {
  const [busy, setBusy] = useState("");
  const [showRes, setShowRes] = useState(false);
  const [form, setForm] = useState({ date: booking.date || "", start_time: booking.start_time || "", mode: "confirm", note: "" });
  const status = booking.status || "pending";
  const canConfirm = status === "pending";

  const confirm = async () => {
    setBusy("confirm");
    try { await api.put(`/appointments/${booking.id}/confirm`); toast.success("Appointment confirmed — client notified."); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const submitReschedule = async () => {
    if (!form.date || !form.start_time) { toast.error("Pick a new date and time."); return; }
    setBusy("res");
    try {
      await api.put(`/appointments/${booking.id}/reschedule`, form);
      toast.success(form.mode === "propose" ? "New time proposed — client will accept or decline." : "Rescheduled — client notified.");
      setShowRes(false); onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  if (status === "cancelled") return null;

  return (
    <div className="flex items-center gap-2" data-testid={`appt-actions-${booking.id}`}>
      {canConfirm && (
        <button onClick={confirm} disabled={busy === "confirm"} data-testid={`appt-confirm-${booking.id}`}
          className="eyw-btn !py-1.5 !px-3 text-xs disabled:opacity-60">
          {busy === "confirm" ? <Loader2 className="animate-spin" size={13} /> : <Check size={13} />} Confirm
        </button>
      )}
      <button onClick={() => setShowRes(true)} data-testid={`appt-reschedule-${booking.id}`}
        className="eyw-btn-ghost !py-1.5 !px-3 text-xs"><CalendarClock size={13} /> Reschedule</button>

      {showRes && (
        <div className="fixed inset-0 z-[120] grid place-items-center px-4 bg-black/40 backdrop-blur-sm" data-testid="reschedule-modal">
          <div className="relative w-full max-w-md bg-[#F9F7F3] rounded-3xl shadow-2xl p-7">
            <button onClick={() => setShowRes(false)} className="absolute top-4 right-4 text-[#4A5D6B] hover:text-[#144F75]"><X size={20} /></button>
            <h3 className="font-serif text-xl text-[#1A242B] mb-1">Reschedule appointment</h3>
            <p className="text-xs text-[#4A5D6B] mb-4">{booking.service_title} · {booking.name}</p>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-[#4A5D6B]">New date</label><input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="reschedule-date" className="eyw-input w-full" /></div>
                <div><label className="text-xs text-[#4A5D6B]">New time</label><input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="reschedule-time" className="eyw-input w-full" /></div>
              </div>
              <div>
                <label className="text-xs text-[#4A5D6B]">How should this apply?</label>
                <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} data-testid="reschedule-mode" className="eyw-input w-full">
                  <option value="confirm">Move it now &amp; notify the client</option>
                  <option value="propose">Propose this time — client must accept</option>
                </select>
              </div>
              <input placeholder="Note to client (optional)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="reschedule-note" className="eyw-input w-full" />
              <button onClick={submitReschedule} disabled={busy === "res"} data-testid="reschedule-submit" className="eyw-btn w-full disabled:opacity-60">
                {busy === "res" ? <Loader2 className="animate-spin" size={16} /> : <CalendarClock size={16} />} {form.mode === "propose" ? "Send proposed time" : "Reschedule & notify"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
